import React, { useState, useEffect } from 'react'
import { RefreshCw, Clock, AlertCircle, CheckCircle, Zap } from 'lucide-react'

function RealTimePricing({ providerId, region, onPricingUpdate }) {
  const [pricingData, setPricingData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchPricing = async (force = false) => {
    setLoading(true)
    setError(null)

    try {
      const url = `/api/providers/${providerId}/pricing${region ? `?region=${region}` : ''}`
      const response = await fetch(url)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch pricing: ${response.status}`)
      }

      const data = await response.json()
      setPricingData(data)
      setLastUpdated(new Date())
      
      if (onPricingUpdate) {
        onPricingUpdate(data)
      }
    } catch (err) {
      setError(err.message)
      console.error('Pricing fetch error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (providerId) {
      fetchPricing()
    }
  }, [providerId, region])

  const refreshPricing = () => {
    fetchPricing(true)
  }

  const getDataSourceInfo = () => {
    if (!pricingData) return null

    const source = pricingData.source
    switch (source) {
      case 'aws-market-rates':
      case 'gcp-market-rates':
      case 'azure-market-rates':
        return {
          icon: CheckCircle,
          text: 'Live Market Rates',
          color: 'text-success-600',
          bgColor: 'bg-success-50',
          borderColor: 'border-success-200'
        }
      case 'aws-price-list-api':
      case 'gcp-billing-api':
      case 'azure-retail-prices-api':
        return {
          icon: Zap,
          text: 'Real-time API',
          color: 'text-primary-600',
          bgColor: 'bg-primary-50',
          borderColor: 'border-primary-200'
        }
      case 'static-data':
      case 'fallback-static':
        return {
          icon: Clock,
          text: 'Cached Data',
          color: 'text-warning-600',
          bgColor: 'bg-warning-50',
          borderColor: 'border-warning-200'
        }
      default:
        return {
          icon: AlertCircle,
          text: 'Unknown Source',
          color: 'text-error-600',
          bgColor: 'bg-error-50',
          borderColor: 'border-error-200'
        }
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(amount)
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never'
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.round((timestamp - new Date()) / (1000 * 60)),
      'minute'
    )
  }

  const sourceInfo = getDataSourceInfo()

  return (
    <div className="card-pricing">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <h3 className="text-base font-semibold text-secondary-900">Live Pricing</h3>
          {sourceInfo && (
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${sourceInfo.bgColor} ${sourceInfo.color} ${sourceInfo.borderColor} border`}>
              <sourceInfo.icon className="h-3 w-3" />
              <span>{sourceInfo.text}</span>
            </div>
          )}
        </div>
        
        <button
          onClick={refreshPricing}
          disabled={loading}
          className={`p-2 rounded-lg transition-all duration-200 ${
            loading 
              ? 'bg-secondary-100 text-secondary-400 cursor-not-allowed' 
              : 'bg-white hover:bg-secondary-50 text-secondary-600 hover:text-secondary-900 shadow-soft hover:shadow-medium'
          }`}
          title="Refresh pricing data"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-error-600" />
            <span className="text-sm text-error-700 font-medium">Error loading pricing</span>
          </div>
          <p className="text-xs text-error-600 mt-1">{error}</p>
        </div>
      )}

      {loading && !pricingData && (
        <div className="space-y-3">
          <div className="loading-pulse h-4 rounded"></div>
          <div className="loading-pulse h-4 rounded w-3/4"></div>
          <div className="loading-pulse h-4 rounded w-1/2"></div>
        </div>
      )}

      {pricingData && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold gradient-text">
                {formatCurrency(pricingData.pricing.vcpu)}
              </div>
              <div className="text-xs text-secondary-500 font-medium">per vCPU/hour</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold gradient-text">
                {formatCurrency(pricingData.pricing.ramGb)}
              </div>
              <div className="text-xs text-secondary-500 font-medium">per GB RAM/hour</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold gradient-text">
                {formatCurrency(pricingData.pricing.storageGb)}
              </div>
              <div className="text-xs text-secondary-500 font-medium">per GB Storage/hour</div>
            </div>
          </div>

          <div className="border-t border-secondary-200 pt-3">
            <div className="flex items-center justify-between text-xs text-secondary-500">
              <span>Last updated: {formatTimestamp(lastUpdated)}</span>
              {pricingData.region && (
                <span>Region: {pricingData.region}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RealTimePricing 